import type { ReactNode } from "react";

import { useDispatch, useSelector } from "react-redux";

import AddAliasOrVlan from "../../AddAliasOrVlan";

import type {
  Expanded,
  SetExpanded,
} from "app/base/components/NodeNetworkTab/NodeNetworkTab";
import { ExpandedState } from "app/base/components/NodeNetworkTab/NodeNetworkTab";
import ActionConfirm from "app/base/components/node/ActionConfirm";
import { actions as machineActions } from "app/store/machine";
import machineSelectors from "app/store/machine/selectors";
import type { Machine } from "app/store/machine/types";
import type { RootState } from "app/store/root/types";
import { NetworkInterfaceTypes } from "app/store/types/enum";
import type { NetworkInterface, NetworkLink } from "app/store/types/node";
import { getLinkInterface, getRemoveTypeText, isAlias } from "app/store/utils";

type Props = {
  expanded: Expanded | null;
  link?: NetworkLink | null;
  nic?: NetworkInterface | null;
  setExpanded: SetExpanded;
  systemId: Machine["system_id"];
};

const NetworkTableConfirmation = ({
  expanded,
  link,
  nic,
  setExpanded,
  systemId,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const machine = useSelector((state: RootState) =>
    machineSelectors.getById(state, systemId)
  );
  if (machine && link && !nic) {
    [nic] = getLinkInterface(machine, link);
  }
  if (!machine || !nic || !expanded) {
    return null;
  }
  const removeTypeText = getRemoveTypeText(machine, nic, link);
  const isAnAlias = isAlias(machine, link);
  const close = () => setExpanded(null);
  let content: ReactNode;
  if (expanded?.content === ExpandedState.REMOVE) {
    content = (
      <ActionConfirm
        closeExpanded={close}
        confirmLabel="Remove"
        eventName={isAnAlias ? "unlinkSubnet" : "deleteInterface"}
        message={`Are you sure you want to remove this ${removeTypeText}?`}
        onConfirm={() => {
          dispatch(machineActions.cleanup());
          if (isAnAlias) {
            if (nic?.id && link?.id) {
              dispatch(
                machineActions.unlinkSubnet({
                  interfaceId: nic?.id,
                  linkId: link?.id,
                  systemId: machine.system_id,
                })
              );
            }
          } else if (nic?.id) {
            dispatch(
              machineActions.deleteInterface({
                interfaceId: nic?.id,
                systemId: machine.system_id,
              })
            );
          }
        }}
        onSaveAnalytics={{
          action: `Remove ${removeTypeText}`,
          category: "Machine network",
          label: "Remove",
        }}
        statusKey={isAnAlias ? "unlinkingSubnet" : "deletingInterface"}
        systemId={machine.system_id}
      />
    );
  } else if (
    [
      ExpandedState.MARK_CONNECTED,
      ExpandedState.MARK_DISCONNECTED,
      ExpandedState.DISCONNECTED_WARNING,
    ].includes(expanded?.content)
  ) {
    const showDisconnectedWarning =
      expanded?.content === ExpandedState.DISCONNECTED_WARNING;
    const markConnected =
      expanded?.content === ExpandedState.MARK_CONNECTED ||
      showDisconnectedWarning;
    const event = markConnected ? "connected" : "disconnected";
    const updateConnection = () => {
      if (nic?.id) {
        dispatch(
          machineActions.updateInterface({
            interface_id: nic?.id,
            link_connected: !!markConnected,
            system_id: machine.system_id,
          })
        );
      }
    };
    let message: ReactNode;
    if (showDisconnectedWarning) {
      message = (
        <>
          This interface is <strong>disconnected</strong>, it cannot be
          configured unless a cable is connected.
          <br />
          If this is no longer true, mark cable as connected.
        </>
      );
    } else {
      message = (
        <>
          This interface was detected as{" "}
          <strong>{nic.link_connected ? "connected" : "disconnected"}</strong>.
          Are you sure you want to mark it as {event}?
          {markConnected ? null : (
            <>
              <br />
              When the interface is disconnected, it cannot be configured.
            </>
          )}
        </>
      );
    }
    content = (
      <ActionConfirm
        closeExpanded={close}
        confirmLabel={`Mark as ${event}`}
        eventName="updateInterface"
        message={message}
        onConfirm={updateConnection}
        onSaveAnalytics={{
          action: `Mark interface as ${event}`,
          category: "Machine network",
          label: "Update",
        }}
        statusKey="updatingInterface"
        submitAppearance={markConnected ? "positive" : "negative"}
        systemId={machine.system_id}
      />
    );
  } else if (expanded?.content === ExpandedState.ADD_ALIAS) {
    content = (
      <AddAliasOrVlan
        close={close}
        interfaceType={NetworkInterfaceTypes.ALIAS}
        nic={nic}
        systemId={systemId}
      />
    );
  } else if (expanded?.content === ExpandedState.ADD_VLAN) {
    content = (
      <AddAliasOrVlan
        close={close}
        interfaceType={NetworkInterfaceTypes.VLAN}
        nic={nic}
        systemId={systemId}
      />
    );
  }
  return <div className="u-flex--grow">{content}</div>;
};

export default NetworkTableConfirmation;
